import { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Paper, Stack, TextField, Typography, MenuItem, Grid, Chip, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import KeyIcon from "@mui/icons-material/Key";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import Swal from "sweetalert2";
import {
  listarCandidatos, obterCandidato, salvarCandidato, redefinirSenhaCandidato,
} from "../../services/candidatoAdminService";
import { getEstados, getCidades } from "../../services/candidatoService";
import { listarEventos } from "../../services/eventoService";
import { mascaraCpf } from "../../utils/documento";
import { fmtData } from "../../utils/data";
import InscricaoAdminDialog from "../Eventos/InscricaoAdminDialog";

const vazio = () => ({
  Codigo: 0, Cpf: "", Nome: "", NomeSocial: "", Email: "", DataNascimento: "", Sexo: "NI",
  Telefone: "", Telefone2: "", Cep: "", CodEstado: "", CodCidade: "", Bairro: "", Endereco: "",
  Numero: "", Complemento: "", AutorizarReceberEmail: true, AutorizarReceberTelefone: true,
  Ativo: true, Senha: "",
});

export default function CandidatoList() {
  const [lista, setLista] = useState([]);
  const [termo, setTermo] = useState("");
  const [carregando, setCarregando] = useState(false);

  const [edit, setEdit] = useState(null);       // cadastro em edição/criação
  const [salvando, setSalvando] = useState(false);
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);

  const [eventos, setEventos] = useState([]);
  const [inscrever, setInscrever] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setLista((await listarCandidatos(termo)).data); }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setCarregando(false); }
  }, [termo]);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { getEstados().then((r) => setEstados(r.data)).catch(() => {}); }, []);
  useEffect(() => { listarEventos().then((r) => setEventos(r.data)).catch(() => {}); }, []);

  // Cidades dependem do estado escolhido, como no cadastro público.
  useEffect(() => {
    if (!edit?.CodEstado) { setCidades([]); return; }
    getCidades(edit.CodEstado).then((r) => setCidades(r.data)).catch(() => setCidades([]));
  }, [edit?.CodEstado]);

  const set = (k, v) => setEdit((p) => ({ ...p, [k]: v }));

  const abrirNovo = () => setEdit(vazio());

  const abrirEdicao = async (c) => {
    try {
      const { data } = await obterCandidato(c.Codigo);
      setEdit({
        ...vazio(), ...data,
        Telefone: data.Telefone1 || "",
        DataNascimento: (data.DataNascimento || "").slice(0, 10),
        CodEstado: data.CodEstado ?? "", CodCidade: data.CodCidade ?? "",
        Senha: "",
      });
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const { data } = await salvarCandidato({
        ...edit,
        CodEstado: edit.CodEstado || null,
        CodCidade: edit.CodCidade || null,
        DataNascimento: edit.DataNascimento || null,
      });
      Swal.fire("Pronto", data.mensagem, "success");
      setEdit(null);
      carregar();
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setSalvando(false); }
  };

  const redefinirSenha = async (c) => {
    const { value: senha } = await Swal.fire({
      title: "Redefinir senha",
      html: `Nova senha de <b>${c.Nome}</b> (CPF ${mascaraCpf(c.Cpf)}).`
        + "<br/><small>Informe ao candidato — ele poderá trocá-la depois no portal.</small>",
      input: "text", inputPlaceholder: "Mínimo 6 caracteres",
      showCancelButton: true, confirmButtonText: "Redefinir", cancelButtonText: "Cancelar",
      inputValidator: (v) => (!v || v.trim().length < 6 ? "A senha deve ter ao menos 6 caracteres." : undefined),
    });
    if (!senha) return;
    try {
      const { data } = await redefinirSenhaCandidato(c.Codigo, senha.trim());
      Swal.fire("Senha redefinida", data.mensagem, "success");
      carregar();
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Candidatos do portal</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<HowToRegIcon />} onClick={() => setInscrever(true)}>
            Inscrever em evento
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>Novo candidato</Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
        <TextField size="small" sx={{ width: 380 }} label="Buscar por nome, CPF ou e-mail"
          value={termo} onChange={(e) => setTermo(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && carregar()} />
        <Button variant="outlined" onClick={carregar} disabled={carregando}>
          {carregando ? "Buscando…" : "Buscar"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Chip label={`${lista.length} candidato(s)`} color="primary" variant="outlined" />
      </Stack>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>CPF</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell align="center">Fichas</TableCell>
              <TableCell align="center">Inscrições</TableCell>
              <TableCell align="center">Situação</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lista.map((c) => (
              <TableRow key={c.Codigo} hover>
                <TableCell>
                  {c.Nome}
                  <Typography variant="caption" color="text.secondary" display="block">
                    Cadastro em {fmtData(c.DtCadastro, "—")}
                  </Typography>
                </TableCell>
                <TableCell>{mascaraCpf(c.Cpf)}</TableCell>
                <TableCell>{c.Email}</TableCell>
                <TableCell>{c.Telefone1 || "—"}</TableCell>
                <TableCell align="center">{c.QtdFichas}</TableCell>
                <TableCell align="center">{c.QtdInscricoes}</TableCell>
                <TableCell align="center">
                  {!c.Ativo ? <Chip size="small" label="Inativo" color="default" />
                    : !c.TemSenha ? <Chip size="small" label="Sem senha" color="warning" variant="outlined" />
                      : <Chip size="small" label="Ativo" color="success" variant="outlined" />}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar cadastro">
                    <IconButton size="small" onClick={() => abrirEdicao(c)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Redefinir senha">
                    <IconButton size="small" color="warning" onClick={() => redefinirSenha(c)}>
                      <KeyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {lista.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">Nenhum candidato encontrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Cadastro — os mesmos campos do cadastro feito pelo próprio candidato */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="md" fullWidth>
        <DialogTitle>{edit?.Codigo ? "Editar candidato" : "Novo candidato"}</DialogTitle>
        <DialogContent dividers>
          {edit && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth label="CPF" placeholder="000.000.000-00"
                  inputProps={{ inputMode: "numeric", maxLength: 14 }}
                  value={mascaraCpf(edit.Cpf)} onChange={(e) => set("Cpf", mascaraCpf(e.target.value))} />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField fullWidth label="Nome completo" inputProps={{ maxLength: 250 }}
                  value={edit.Nome} onChange={(e) => set("Nome", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Nome social (opcional)" value={edit.NomeSocial || ""}
                  onChange={(e) => set("NomeSocial", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField fullWidth type="date" label="Nascimento" InputLabelProps={{ shrink: true }}
                  value={edit.DataNascimento || ""} onChange={(e) => set("DataNascimento", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField select fullWidth label="Sexo" value={edit.Sexo || "NI"}
                  onChange={(e) => set("Sexo", e.target.value)}>
                  <MenuItem value="M">Masculino</MenuItem>
                  <MenuItem value="F">Feminino</MenuItem>
                  <MenuItem value="NI">Não informar</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="E-mail" inputProps={{ maxLength: 100 }}
                  value={edit.Email} onChange={(e) => set("Email", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField fullWidth label="Telefone" inputProps={{ maxLength: 20 }}
                  value={edit.Telefone || ""} onChange={(e) => set("Telefone", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField fullWidth label="Telefone 2" inputProps={{ maxLength: 20 }}
                  value={edit.Telefone2 || ""} onChange={(e) => set("Telefone2", e.target.value)} />
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField fullWidth label="CEP" inputProps={{ maxLength: 10 }}
                  value={edit.Cep || ""} onChange={(e) => set("Cep", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField select fullWidth label="Estado" value={edit.CodEstado || ""}
                  onChange={(e) => { set("CodEstado", e.target.value); set("CodCidade", ""); }}>
                  <MenuItem value="">—</MenuItem>
                  {estados.map((es) => <MenuItem key={es.CodUf} value={es.CodUf}>{es.Sigla} — {es.Nome}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth label="Cidade" value={edit.CodCidade || ""}
                  disabled={!edit.CodEstado} onChange={(e) => set("CodCidade", e.target.value)}>
                  <MenuItem value="">—</MenuItem>
                  {cidades.map((c) => <MenuItem key={c.CodMunicipio} value={c.CodMunicipio}>{c.Municipio}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField fullWidth label="Endereço" inputProps={{ maxLength: 250 }}
                  value={edit.Endereco || ""} onChange={(e) => set("Endereco", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 4, sm: 2 }}>
                <TextField fullWidth label="Número" value={edit.Numero || ""}
                  onChange={(e) => set("Numero", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 8, sm: 5 }}>
                <TextField fullWidth label="Bairro" inputProps={{ maxLength: 250 }}
                  value={edit.Bairro || ""} onChange={(e) => set("Bairro", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label="Complemento" value={edit.Complemento || ""}
                  onChange={(e) => set("Complemento", e.target.value)} />
              </Grid>

              {/* Cadastro novo precisa de senha, senão o candidato não consegue entrar. */}
              {!edit.Codigo && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Senha inicial" value={edit.Senha || ""}
                    onChange={(e) => set("Senha", e.target.value)}
                    helperText="Mínimo 6 caracteres — informe ao candidato, que poderá trocá-la no portal." />
                </Grid>
              )}

              <Grid size={{ xs: 12 }} sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <FormControlLabel label="Aceita receber e-mails"
                  control={<Checkbox checked={!!edit.AutorizarReceberEmail}
                    onChange={(e) => set("AutorizarReceberEmail", e.target.checked)} />} />
                <FormControlLabel label="Aceita contato por telefone"
                  control={<Checkbox checked={!!edit.AutorizarReceberTelefone}
                    onChange={(e) => set("AutorizarReceberTelefone", e.target.checked)} />} />
                <FormControlLabel label="Cadastro ativo"
                  control={<Checkbox checked={!!edit.Ativo} onChange={(e) => set("Ativo", e.target.checked)} />} />
              </Grid>

              {edit.Codigo > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    Para trocar a senha, use o botão <b>Redefinir senha</b> na listagem.
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando…" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inscrição em evento — reaproveita o fluxo já existente, com as mesmas regras */}
      <InscricaoAdminDialog
        open={inscrever}
        onClose={() => setInscrever(false)}
        eventos={eventos}
        onInscrito={carregar}
      />
    </Box>
  );
}
